// deps
import React, { ReactElement, useCallback, useEffect, useMemo } from "react";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";

// interface
import { TurboStripData, TurboStripEventAction, TurboStripType } from "@core-ui/interfaces/Turbo";
import { ACCESSIBILITY_ROLES } from "@swiggy-private/core-types";

// dls
import { default as Svg12 } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronRight/12";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// constants
import { STAR_ICON, STRIP_DIMENSIONS, SPLIT_PAY_ICON_DIMENSIONS, DEFAULT_STRIP_A11Y_ACTION } from "./constants";

// styles
import {
    Chevron,
    CtaTextWrapper,
    InfoWrapper,
    RightContent,
    Strip,
    StripContent,
    StripWrapper,
    StripWrapperParent,
} from "./styles";

// component
import TurboStripContent from "./components/TurboStripContent";
import { NewTag } from "@core-ui/widgets/new-tag";
import AriaHidden from "@core-ui/components/accessibility/aria-hidden/AriaHidden";

// helpers
import { handleKeyboardActivation } from "@core-ui/helpers/Dom";

export interface TurboStripProps {
    data: TurboStripData;
    onClickTurboStrip?: TurboStripEventAction;
    onImpressionTurboStrip?: TurboStripEventAction;
    isTurboV2?: boolean;
    isTurbo?: boolean;
}

function TurboStrip(props: TurboStripProps): ReactElement | null {
    const { onClickTurboStrip, onImpressionTurboStrip, isTurboV2, data, isTurbo = true } = props;

    const { stripMsg = "", isNewBanner = true, bannerId = "", iconUrl = "", type = TurboStripType.STATIC, cta } = data;

    const iconUrlCloudId = isEmpty(iconUrl) ? STAR_ICON : iconUrl;

    const isStaticInverse = type === TurboStripType.STATIC_INVERSE;

    const handleOnClick = useCallback(() => {
        !isEmpty(onClickTurboStrip) && onClickTurboStrip(isNewBanner, bannerId, stripMsg);
    }, [isNewBanner, bannerId, stripMsg, onClickTurboStrip]);

    // The visible strip text is aria-hidden (to avoid reading partial animations), so the
    // focusable strip needs its own accessible name describing the message and the tap action.
    const stripAccessibilityLabel = useMemo(() => {
        const cleanedMsg = stripMsg.replace(/\[\[|\]\]/g, "").trim();
        const action = cta?.text || DEFAULT_STRIP_A11Y_ACTION;
        return isEmpty(cleanedMsg) ? action : `${cleanedMsg}, ${action}`;
    }, [stripMsg, cta]);

    const splitPayIconDimensions = useMemo(() => {
        if (type === TurboStripType.DYNAMIC_SPLIT_PAY_INFO) {
            return SPLIT_PAY_ICON_DIMENSIONS;
        }
        return STRIP_DIMENSIONS;
    }, [type]);

    const iconComp = useCallback(() => {
        if (type === TurboStripType.STATIC_INVERSE) {
            return null;
        }

        if (isNewBanner) {
            return <NewTag skipShimmer />;
        }
        return <Image {...splitPayIconDimensions} cloudinaryId={iconUrlCloudId} alt="Turbo Strip Icon" />;
    }, [iconUrlCloudId, isNewBanner, splitPayIconDimensions, type]);

    useEffect(() => {
        !isEmpty(onImpressionTurboStrip) &&
            !isEmpty(stripMsg) &&
            onImpressionTurboStrip(isNewBanner, bannerId, stripMsg);
    }, [onImpressionTurboStrip, isNewBanner, bannerId, stripMsg]);

    if (isEmpty(stripMsg)) return null;

    return (
        <StripWrapperParent
            type={type}
            onClick={handleOnClick}
            role={ACCESSIBILITY_ROLES.BUTTON}
            tabIndex={0}
            aria-label={stripAccessibilityLabel}
            onKeyDown={handleKeyboardActivation(handleOnClick)}
        >
            <StripWrapper isTurboV2={isTurboV2} isTurbo={isTurbo} type={type}>
                <StripContent>
                    <InfoWrapper>
                        <AriaHidden as="div">{iconComp()}</AriaHidden>
                        <TurboStripContent data={data} />
                    </InfoWrapper>
                    {!isEmpty(cta) ? (
                        <CtaTextWrapper
                            variant="V2.overline.neutral.bold"
                            color="text_inverse_Highest_Emphasis"
                            maxLines={1}
                            aria-hidden="true"
                        >
                            {cta.text}
                        </CtaTextWrapper>
                    ) : (
                        <RightContent aria-hidden="true">
                            <Chevron />
                            <GenericIcon Icon={Svg12} fillColor="text_High_Emphasis" />
                        </RightContent>
                    )}
                </StripContent>
                {!isStaticInverse && <Strip />}
            </StripWrapper>
        </StripWrapperParent>
    );
}

export default React.memo(TurboStrip);
